import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IconButton,
  Box,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';

import { IconBellRinging } from '@tabler/icons';
import { Stack } from '@mui/system';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import NotificationService from 'src/store/apps/services/NotificationService';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotificationStats, notificationReceived, notificationSelector } from 'src/store/apps/slices/NotificationSlice';

// Add relativeTime plugin to dayjs
dayjs.extend(relativeTime);

const Notifications = () => {
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { notiStats } = useSelector(notificationSelector);

  const scrollableContainerRef = useRef(null);
  const observerRef = useRef(null);

  
  useEffect(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'BACKGROUND_NOTIFICATION') {
          console.log('Background notification received in component');
          dispatch(notificationReceived());
          
          // // Optionally refresh notifications list
          // if (anchorEl2) {
          //   fetchNotifications(1, false);
          // }
        }
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, [dispatch, anchorEl2]);

  useEffect(() => {
      dispatch(fetchNotificationStats());
  }, []);

  // Fetch initial notifications when dropdown opens
  const fetchNotifications = useCallback(async (pageNum = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setIsFetching(true);
      } else {
        setLoading(true);
      }
      
      const response = await NotificationService.fetchNotification({
        perPage: 20,
        page: pageNum,
      });
      
      if (response?.data?.status === 'Success') {
        const data = response.data.data;
        
        if (isLoadMore) {
          // Append new notifications for infinite scroll
          setNotifications(prev => [...prev, ...data.notifications]);
        } else {
          // Replace notifications for initial load
          setNotifications(data.notifications);
        }
      
        setPagination(data.pagination);
        setPage(data.pagination.page);
        dispatch(fetchNotificationStats());
        // Update hasNextPage
        setHasNextPage(data.pagination.page < data.pagination.lastPage);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (anchorEl2 && notifications.length === 0 && !loading) {
      fetchNotifications(1, false);
    }
  }, [anchorEl2, notifications.length, loading, fetchNotifications]);

  // Handle loading more notifications
  const handleLoadMore = useCallback(() => {
    if (!loading && !isFetching && hasNextPage && pagination) {
      const nextPage = pagination.page + 1;
      fetchNotifications(nextPage, true);
    }
  }, [loading, isFetching, hasNextPage, pagination, fetchNotifications]);

  // Setup infinite scroll using Intersection Observer
  useEffect(() => {
    if (!scrollableContainerRef.current || !hasNextPage || loading || isFetching) return;

    const options = {
      root: scrollableContainerRef.current,
      rootMargin: '0px 0px 100px 0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        handleLoadMore();
      }
    }, options);

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [handleLoadMore, hasNextPage, loading, isFetching]);

  const handleClick2 = async (event) => {
    setAnchorEl2(event.currentTarget);
    // Refresh notifications when opening the dropdown
    if (notifications.length === 0) {
      await fetchNotifications(1, false);
    }
  };

  const handleClose2 = () => {
    setAnchorEl2(null);
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark notification as read if it's unread
      if (!notification.is_read) {
        await NotificationService.markAsRead(notification.id);
        // Update local state
        setNotifications(prev => 
          prev.map(item => 
            item.id === notification.id 
              ? { ...item, is_read: true } 
              : item
          )
        );
        dispatch(fetchNotificationStats());
      }
      console.log('Notification clicked:', notification);
      navigate(`/orders/${notification.order?.id || ''}`);
      // handleClose2();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      handleClose2();
    }
  };

  // Mark all as read function
  const handleMarkAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      // Update all notifications to read
      setNotifications(prev => 
        prev.map(item => ({ ...item, is_read: true }))
      );
      dispatch(fetchNotificationStats());
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const getNotificationIcon = (notification) => {
    const type = notification.type || '';
    if (type.includes('order') || type.includes('Order')) {
      return '';
    }
    if (type.includes('message') || type.includes('chat')) {
      return '';
    }
    return notification.avatar || '/default-avatar.png';
  };

  return (
    <Box>
      <IconButton
        size="large"
        aria-label={`show ${notiStats?.unread_count || 0} new notifications`}
        color="inherit"
        aria-controls="msgs-menu"
        aria-haspopup="true"
        sx={{
          ...(anchorEl2 && {
            color: 'primary.main',
          }),
        }}
        onClick={handleClick2}
      >
        <Badge 
          badgeContent={notiStats?.unread_count || 0} 
          color="primary"
          max={99}
        >
          <IconBellRinging size="21" stroke="1.5" />
        </Badge>
      </IconButton>

      <Menu
        id="msgs-menu"
        anchorEl={anchorEl2}
        keepMounted
        open={Boolean(anchorEl2)}
        onClose={handleClose2}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        sx={{
          '& .MuiMenu-paper': {
            width: '450px',
            maxHeight: '500px',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Stack 
          direction="row" 
          py={2} 
          px={4} 
          justifyContent="space-between" 
          alignItems="center"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography variant="h6">Notifications</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip 
              label={`${notiStats?.unread_count || 0} new`} 
              color="primary" 
              size="small" 
            />
            {notiStats?.unread_count > 0 && (
              <Button 
                size="small" 
                onClick={handleMarkAllAsRead}
                sx={{ ml: 1 }}
              >
                Mark all read
              </Button>
            )}
          </Stack>
        </Stack>
        
        <Box 
          ref={scrollableContainerRef}
          sx={{ 
            flex: 1,
            overflow: 'auto',
            maxHeight: '385px',
          }}
        >
          {notifications.length === 0 && !loading ? (
            <Box py={4} textAlign="center">
              <Typography color="textSecondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            <>
              {notifications.map((notification, index) => (
                <MenuItem 
                  key={notification.id || index}
                  sx={{ 
                    py: 2, 
                    px: 4,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                    position: 'relative',
                    display: 'block',
                    textAlign: 'left',
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      src={getNotificationIcon(notification)}
                      alt="Notification"
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: notification.is_read ? 'grey.300' : 'primary.light',
                        flexShrink: 0,
                      }}
                    >
                      {!notification.avatar && (
                        <IconBellRinging size={20} />
                      )}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="flex-start"
                        spacing={1}
                        mb={1}
                      >
                        <Typography
                          variant="subtitle2"
                          color="textPrimary"
                          fontWeight={600}
                          sx={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ 
                            whiteSpace: 'nowrap', 
                            ml: 1,
                            flexShrink: 0,
                          }}
                        >
                          {notification.formatted_date}
                        </Typography>
                      </Stack>
                      <Typography
                        color="textSecondary"
                        variant="body2"
                        sx={{
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: 1.5,
                        }}
                      >
                        {notification.message}
                      </Typography>
                    </Box>
                  </Stack>
                  {!notification.is_read && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                      }}
                    />
                  )}
                </MenuItem>
              ))}

              {/* Loading indicator */}
              {(loading || isFetching) && (
                <Box py={2} textAlign="center">
                  <CircularProgress size={24} />
                </Box>
              )}

              {/* Observer target for infinite scroll */}
              {hasNextPage && !loading && !isFetching && (
                <div ref={observerRef} style={{ height: '20px' }} />
              )}

              {/* No more notifications */}
              {!hasNextPage && notifications.length > 0 && (
                <Box py={2} textAlign="center">
                  <Typography variant="caption" color="textSecondary">
                    No more notifications
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Menu>
    </Box>
  );
};

export default Notifications;